import { createContext, useCallback, useContext, useState } from 'react';
import { useAzureProvider } from 'src/azure/context';
import { checkProjectAuthorizationService } from 'src/services/appgami-api/check-project-authorization';

interface AutomationRuleSettingsContextType {
	spHasAccess: boolean;
  userHasAccess: boolean;
	setSpHasAccess: (spHasAccess: boolean) => void;
  setUserHasAccess: (userHasAccess: boolean) => void;
	checkProjectAuthorization: () => Promise<{
		userAuthorization: boolean;
		servicePrincipalAuthorization: boolean;
	} | null>;
}


export const AutomationRuleSettingsContext = createContext({} as AutomationRuleSettingsContextType);
export const useAutomationRuleSettings = () => useContext(AutomationRuleSettingsContext);

export const AutomationRuleSettingsProvider = ({ children }: { children: React.ReactNode }) => {
	const { applicationContext } = useAzureProvider();

	const [state, setState] = useState({
		spHasAccess: false,
		userHasAccess: false,
	});

	const changeState = useCallback((stateInput: Partial<typeof state>) => {
		setState((prevState) => ({...prevState, ...stateInput}));
	}, []);

	const setSpHasAccess = useCallback((spHasAccess: boolean) => {
		changeState({ spHasAccess });
	}, [changeState]);

	const setUserHasAccess = useCallback((userHasAccess: boolean) => {
		changeState({ userHasAccess });
	}, [changeState]);

	const checkProjectAuthorization = useCallback(async () => {
		const response = await checkProjectAuthorizationService({
			organizationName: applicationContext.organizationName,
		});

		if (response.type === 'success') {
			changeState({
				spHasAccess: response.data.servicePrincipalAuthorization,
				userHasAccess: response.data.userAuthorization,
			});

			return {
				userAuthorization: response.data.userAuthorization,
				servicePrincipalAuthorization: response.data.servicePrincipalAuthorization,
			};
		}

		return null;
	}, [
		applicationContext.projectId,
		applicationContext.organizationName,
	]);

	return (
		<AutomationRuleSettingsContext.Provider
			value={{
				spHasAccess: state.spHasAccess,
				userHasAccess: state.userHasAccess,
				setSpHasAccess,
				setUserHasAccess,
				checkProjectAuthorization
			}}
		>
			{children}
		</AutomationRuleSettingsContext.Provider>
	);
};